import { NcpError, boot, bridge } from "./bridge";
import type { PageEmbed, ToolHandler } from "./bridge";
import type { CapabilityInputs, CapabilityName, CapabilityOutputs } from "./generated/capabilities";
import type { HostClassificationArgs, HostDisplay, HostSession, PageRoute } from "./protocol";
import type { Sensitivity } from "./desk";
import type {
  AgentRunEvent, AgentRunOptions, AgentRunOutcome, ChatEvent, ChatThread, ChatThreadMessage, ComputeOutcome, DataAnswer, DataDataset, DataQuery, DataResult, DataRow,
  DataSearchHit, GenerateInput, GenerateResult, ImportOptions, IndicatorDiscovery, MemoryItem, MentionSummary, ModelInfo, ModelProposal, ModelRunDetail, ModelRunOutcome,
  ModelRunSummary, PageMode, PageRecord, PlatformContext, PlatformEvent, ScenarioBatchDetail, ScenarioBatchEvent, ScenarioBatchSpec, ScenarioBatchSummary, ScenarioResult,
  SeriesGap, SourceCandidate, SourceHealth, SourceListItem, SourceProposal, SourceProposed, SourceRunResult, SourceTestResult, TargetWrite, UiBlock, UploadExtraction,
  WebDocument, WebHit,
} from "./types";

export * from "./types";
export type { HostClassificationArgs, HostDisplay, HostSession, PageRoute } from "./protocol";
export type { CapabilityInfo, CapabilityInputs, CapabilityName, CapabilityOutputs, CapabilitySideEffect } from "./generated/capabilities";
export { CAPABILITIES, MUST_DECLARE } from "./generated/capabilities";

/** ncp.call: typed for the platform's capabilities (generated by pnpm sdk:gen), open for any other name. */
export interface CallCapability {
  <K extends CapabilityName>(capability: K, input: CapabilityInputs[K], signal?: AbortSignal): Promise<CapabilityOutputs[K]>;
  <T = unknown>(capability: string, input?: unknown, signal?: AbortSignal): Promise<T>;
}
export { NcpError, boot };
export type { PageEmbed, ToolHandler };

// ------------------------------------------------------------------ page state shared with agents

let pageState: Record<string, unknown> = {};
let stateTimer: ReturnType<typeof setTimeout> | undefined;

function pushState(): void {
  clearTimeout(stateTimer);
  stateTimer = setTimeout(() => void bridge.host("state", pageState).catch(() => undefined), 250);
}

// ------------------------------------------------------------------ chat

export interface ChatOptions {
  threadId?: string | null;
  /** What the user is looking at; defaults to the page state set with ncp.state.set. Sent as data, never as instructions. */
  state?: unknown;
  onEvent?: (event: ChatEvent) => void;
  onToken?: (text: string, all: string) => void;
  onBlock?: (block: UiBlock) => void;
  signal?: AbortSignal;
}

export interface ChatResult {
  threadId: string | null;
  messageId: string | null;
  content: string;
  blocks: UiBlock[];
  error: string | null;
}

async function chat(agent: string, message: string, options: ChatOptions = {}): Promise<ChatResult> {
  const result: ChatResult = { threadId: options.threadId ?? null, messageId: null, content: "", blocks: [], error: null };
  await bridge.chat(agent, message, options.threadId, options.state ?? pageState, (raw) => {
    const event = raw as ChatEvent;
    switch (event.type) {
      case "token":
        result.content += event.text;
        options.onToken?.(event.text, result.content);
        break;
      case "status":
        if (event.stage === "thread" && event.detail) result.threadId = event.detail;
        break;
      case "block": {
        const at = result.blocks.findIndex((b) => b.id === event.blockId);
        const block = { ...event.block, id: event.blockId };
        if (at >= 0) result.blocks[at] = block;
        else result.blocks.push(block);
        options.onBlock?.(block);
        break;
      }
      case "final":
        result.messageId = event.messageId;
        result.content = event.content || result.content;
        break;
      case "error":
        result.error = event.message;
        break;
    }
    options.onEvent?.(event);
  }, options.signal);
  return result;
}

// ------------------------------------------------------------------ collections

export interface CollectionListOptions {
  key?: string;
  matterId?: string;
  mine?: boolean;
  where?: Record<string, unknown>;
  limit?: number;
  text?: string;
}

export interface Collection<T> {
  list(options?: CollectionListOptions): Promise<PageRecord<T>[]>;
  get(id: string): Promise<PageRecord<T>>;
  byKey(key: string): Promise<PageRecord<T> | null>;
  put(data: T, options?: { id?: string; key?: string; revision?: number; matterId?: string }): Promise<PageRecord<T>>;
  delete(id: string): Promise<boolean>;
}

function collection<T = Record<string, unknown>>(name: string): Collection<T> {
  return {
    list: async (options = {}) => (await bridge.call<{ records: PageRecord<T>[] }>("collections.list", { collection: name, ...options })).records,
    get: (id) => bridge.call<PageRecord<T>>("collections.get", { collection: name, id }),
    byKey: async (key) => (await bridge.call<{ records: PageRecord<T>[] }>("collections.list", { collection: name, key, limit: 1 })).records[0] ?? null,
    put: (data, options = {}) => bridge.call<PageRecord<T>>("collections.put", { collection: name, data, ...options }),
    delete: async (id) => (await bridge.call<{ deleted: boolean }>("collections.delete", { collection: name, id })).deleted,
  };
}

// ------------------------------------------------------------------ the SDK

const toArrayBuffer = async (data: Blob | ArrayBuffer | string): Promise<ArrayBuffer> =>
  typeof data === "string" ? new TextEncoder().encode(data).buffer as ArrayBuffer : data instanceof Blob ? data.arrayBuffer() : data;

async function toBase64(data: Blob | ArrayBuffer): Promise<string> {
  const bytes = new Uint8Array(data instanceof Blob ? await data.arrayBuffer() : data);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

type Upload = File | Blob | { name: string; data: ArrayBuffer; type?: string };

async function upload(file: Upload) {
  const named = file as { name?: string; data?: ArrayBuffer; type?: string };
  const data = named.data instanceof ArrayBuffer ? named.data : (file as Blob);
  return { fileName: named.name || "upload", contentBase64: await toBase64(data), contentType: named.type || undefined };
}

/** Where ncp.ui.open goes: another page, a matter, a platform path (/matters/…, opened in the workspace) or an outside link (new tab). */
export type OpenTarget =
  | { page: string; params?: Record<string, string>; path?: string; space?: string }
  /** A matter, at one of its tabs; params reach the page on that tab as its route (`ncp.nav.route().params`). */
  | { matter: string; tab?: string; params?: Record<string, string> }
  /** Starts one of the space's use cases: the workspace's start dialog opens with these parameters over the use case's own. */
  | { useCase: string; params?: Record<string, string>; title?: string; summary?: string }
  | { url: string };

export const ncp = {
  boot,
  /** Resolves once the host has accepted the page; rejects when the page is opened outside the platform. */
  session: bridge.session as Promise<HostSession>,
  get locale() {
    return bridge.locale;
  },
  get dir() {
    return bridge.locale === "ar" ? "rtl" : "ltr";
  },
  /**
   * For the workspace host only: page-kit drawn in the host itself (UI blocks in the copilot's answers) calls the open page's session
   * through `host` instead of a frame's bridge.
   */
  embed: (host: PageEmbed) => bridge.embed(host),
  /** Tells the host the first view is drawn, so it removes its loading state. */
  ready: () => bridge.ready(),
  /** Any capability by name, typed from the generated catalogue; prefer the helpers below where there is one. */
  call: ((capability: string, input?: unknown, signal?: AbortSignal) => bridge.call(capability, input, signal)) as CallCapability,
  stream: (capability: string, input: unknown, onChunk: (chunk: unknown) => void, signal?: AbortSignal) => bridge.stream(capability, input, onChunk, signal),

  data: {
    catalog: async (domain?: string) => (await bridge.call<{ domains: string[]; datasets: DataDataset[] }>("data.catalog", { domain })),
    query: <R extends DataRow = DataRow>(query: DataQuery, signal?: AbortSignal) => bridge.call<DataResult<R>>("data.query", query, signal),
    /**
     * The same query as an Arrow IPC stream, for large tables: hand it to apache-arrow (tableFromIPC) or DuckDB-WASM
     * (insertArrowFromIPCStream). Dataset, total, truncation, lineage, labels and units ride in the schema metadata (ncp:*).
     */
    arrow: (query: DataQuery, signal?: AbortSignal) => bridge.arrow("data.query", query, signal),
    search: async (query: string, options: { domains?: string[]; datasets?: string[]; take?: number } = {}) =>
      (await bridge.call<{ query: string; hits: DataSearchHit[] }>("data.search", { query, ...options })).hits,
    get: <T = unknown>(dataset: string, ref: string) => bridge.call<T>("data.get", { dataset, ref }),
    ask: (question: string, options: { datasets?: string[]; asOf?: string } = {}) => bridge.call<DataAnswer>("data.ask", { question, ...options }),
    /**
     * Reads a file (a picked File, or bytes) without storing it: workbook sheets with profiled columns, the statistical series the warehouse
     * recognises, a document's text and tables, and a proposed column map for ncp.data.import.
     */
    extract: async (file: Upload, options: { sheet?: string; headerRow?: number; ai?: boolean; hint?: string } = {}) =>
      bridge.call<UploadExtraction>("data.extract", { ...(await upload(file)), ...options }),
    /** Loads a file where options.target says (stats:, page:, knowledge: or publications), with the map from extract as it is or corrected. */
    import: async (file: Upload, options: ImportOptions) => bridge.call<TargetWrite & { documentId?: string }>("data.import", { ...(await upload(file)), ...options }),
    /**
     * Calls onChange when the dataset changes (platform events through the host, or polling for live sources). A burst of events (a
     * refresh revising many series) becomes one call.
     */
    subscribe(dataset: string, onChange: (event: PlatformEvent | { type: "poll" }) => void): () => void {
      let cleanup: (() => void) | undefined;
      let stopped = false;
      void bridge.call<{ topics: string[]; pollSeconds: number }>("data.subscribe", { dataset }).then(({ topics, pollSeconds }) => {
        if (stopped) return;
        let pending: ReturnType<typeof setTimeout> | undefined;
        const off = bridge.on("platform", (payload) => {
          const e = payload as PlatformEvent;
          if (!e?.type || !topics.includes(e.type)) return;
          clearTimeout(pending);
          pending = setTimeout(() => onChange(e), 400);
        });
        const timer = pollSeconds > 0 ? setInterval(() => onChange({ type: "poll" }), pollSeconds * 1000) : undefined;
        cleanup = () => {
          off();
          clearTimeout(pending);
          if (timer) clearInterval(timer);
        };
      }, () => undefined);
      return () => {
        stopped = true;
        cleanup?.();
      };
    },
  },

  collections: collection,
  datasets: {
    put: (input: { key: string; name: string; nameAr?: string; columns: Array<{ key: string; label: string; type: string; unit?: string }>; rows: DataRow[]; lineage?: unknown[]; origin?: string; classification?: string }) =>
      bridge.call<{ dataset: string; ref: string; rows: number; refreshedAt: string }>("datasets.put", input),
  },

  ai: {
    generate: <J = unknown>(input: GenerateInput) => bridge.call<GenerateResult<J>>("ai.generate", input),
    /** A turn with one of the page's agents: streams tokens, UI blocks and tool steps; the agent can call the page's client tools. */
    chat,
    threads: (agent: string) => bridge.host<ChatThread[]>("threads", { agent }),
    messages: (threadId: string) => bridge.host<ChatThreadMessage[]>("messages", { threadId }),
  },
  agents: {
    run: (agent: string, brief: string, options: AgentRunOptions = {}) =>
      bridge.call<AgentRunOutcome>("agents.run", { agent, brief, ...options }),
    /** A long run with its progress: every step the agent takes as it works, then the outcome (and what it saved, with saveTo). */
    stream: async (agent: string, brief: string, onEvent: (event: AgentRunEvent) => void, options: Omit<AgentRunOptions, "wait"> & { signal?: AbortSignal } = {}) => {
      const { signal, ...rest } = options;
      let outcome: AgentRunOutcome | null = null;
      await bridge.stream("agents.run", { agent, brief, ...rest }, (chunk) => {
        const event = chunk as AgentRunEvent;
        if (event.type === "result") outcome = event;
        onEvent(event);
      }, signal);
      return outcome as AgentRunOutcome | null;
    },
  },
  /** Finding data the platform does not hold yet: what a study needs and where it is, new sources, and the Source Scout. */
  discover: {
    /** For each indicator: held, partial (countries missing), findable (an API or registry has it) or missing, with a suggestion. */
    data: async (indicators: string[], options: { countries?: string[]; from?: string; to?: string } = {}) =>
      (await bridge.call<{ indicators: IndicatorDiscovery[] }>("discover.data", { indicators, ...options })).indicators,
    /** Publishers, SDMX registries and (with web) the open web, scored, each with the source definition it would become. Needs the space's web grant. */
    sources: async (topic: string, options: { countries?: string[]; kinds?: string[]; maxResults?: number; web?: boolean } = {}) =>
      (await bridge.call<{ candidates: SourceCandidate[] }>("discover.sources", { topic, ...options })).candidates,
    /**
     * The Source Scout on a brief ("monthly retail prices for the GCC"): it searches, writes and tests recipes, and proposes the sources that
     * work; an economic expert approves them before anything is registered. With onEvent, every step it takes as it works.
     */
    scout: (brief: string, onEvent?: (event: AgentRunEvent) => void, options: Omit<AgentRunOptions, "wait"> & { signal?: AbortSignal } = {}) =>
      onEvent ? ncp.agents.stream("source-scout", brief, onEvent, options) : ncp.agents.run("source-scout", brief, { ...options, wait: true }),
  },
  /** Search and read the open web through the platform's egress gateway (audited, personal data never leaves). Needs the space's web grant. */
  web: {
    search: async (objective: string, options: { queries?: string[]; maxResults?: number; domains?: string[]; locale?: string } = {}) =>
      (await bridge.call<{ provider: string | null; hits: WebHit[] }>("web.search", { objective, ...options })).hits,
    extract: async (urls: string[], options: { objective?: string; fullContent?: boolean; maxChars?: number } = {}) =>
      (await bridge.call<{ provider: string; documents: WebDocument[] }>("web.extract", { urls, ...options })).documents,
  },
  /** The registry of data sources: what is read, from where, how fresh it is, and drafts of new ones. */
  sources: {
    list: async (options: { q?: string; topic?: string; kind?: string; includeDrafts?: boolean } = {}) =>
      (await bridge.call<{ sources: SourceListItem[] }>("sources.list", options)).sources,
    /** Registers a draft (or a new version of the source with that key), tests it, and asks the reviewers to release it. */
    propose: (proposal: SourceProposal) => bridge.call<SourceProposed>("sources.propose", proposal),
    /** A sample read of a registered source or of a proposal; nothing is written. */
    test: (target: { key: string; version?: number } | { proposal: SourceProposal }) => bridge.call<SourceTestResult>("sources.test", target),
    run: (key: string, options: { wait?: boolean } = {}) => bridge.call<SourceRunResult>("sources.run", { key, ...options }),
    health: (options: { gaps?: boolean; maxGaps?: number } = {}) =>
      bridge.call<{ sources: SourceHealth[]; gaps: SeriesGap[] | null }>("sources.health", options),
  },
  /** Mentions of terms in news and (with a licensed listening service) public social posts, summarised by channel, day and sentiment. */
  listen: {
    mentions: (terms: string[], options: { languages?: string[]; since?: string; maxResults?: number; channels?: string[] } = {}) =>
      bridge.call<MentionSummary>("listen.mentions", { terms, ...options }),
  },
  /** The page agent's notes about the person and the space (preferences, facts, working notes), kept with retention rules. */
  memory: {
    remember: (key: string, value: string, options: { kind?: "note" | "preference" | "fact"; scope?: "user" | "space"; days?: number; agent?: string } = {}) =>
      bridge.call<{ key: string; kind: string; scope: string; expiresAt: string | null; replaced: boolean }>("memory.remember", { key, value, ...options }),
    recall: async (options: { q?: string; kind?: string; scope?: "user" | "space" | "all"; take?: number; agent?: string } = {}) =>
      (await bridge.call<{ items: MemoryItem[] }>("memory.recall", options)).items,
    forget: async (key: string, options: { scope?: "user" | "space"; agent?: string } = {}) =>
      (await bridge.call<{ forgotten: boolean }>("memory.forget", { key, ...options })).forgotten,
  },
  /**
   * The model register: Python, JavaScript and Excel models whose inputs are bound to datasets. run is the official run (outputs into the
   * warehouse as model-<key>); evaluate is a what-if that writes nothing. Every run keeps its inputs, parameters, outputs and code hash.
   */
  models: {
    list: async (options: { q?: string; space?: string; page?: string; key?: string; includeDrafts?: boolean } = {}) =>
      (await bridge.call<{ models: ModelInfo[] }>("models.list", options)).models,
    run: (model: string, options: { parameters?: Record<string, unknown>; wait?: boolean; version?: number } = {}) =>
      bridge.call<ModelRunOutcome | { queued: true; model: string }>("models.run", { model, ...options }),
    evaluate: (model: string, options: { parameters?: Record<string, unknown>; inputs?: Record<string, unknown>; version?: number } = {}) =>
      bridge.call<Omit<ModelRunOutcome, "model" | "version" | "written">>("models.evaluate", { model, ...options }),
    runs: async (options: { model?: string; status?: string; trigger?: string; limit?: number } = {}) =>
      (await bridge.call<{ runs: ModelRunSummary[] }>("models.runs", options)).runs,
    runDetail: async (id: string) => (await bridge.call<{ run: ModelRunDetail }>("models.runs", { id })).run,
    /** Drafts a model, or the next version of one; it runs only as a what-if until it is released. */
    propose: (proposal: ModelProposal) => bridge.call<{ key: string; version: number; status: string; link: string }>("models.propose", proposal),
  },
  /**
   * Scenario batches: 1 to 10,000 seeded runs of a model (or the built-in projection) over uncertain assumptions, summarised as
   * distributions, threshold breaches and the shocks that drive each outcome. With onProgress, the batch streams its progress.
   */
  scenarios: {
    batch: async (spec: ScenarioBatchSpec, options: { onProgress?: (completed: number, runs: number) => void; onEvent?: (event: ScenarioBatchEvent) => void; signal?: AbortSignal } = {}) => {
      if (!options.onProgress && !options.onEvent) return bridge.call<ScenarioResult>("scenarios.batch", spec, options.signal);
      let result: ScenarioResult | null = null;
      await bridge.stream("scenarios.batch", spec, (chunk) => {
        const event = chunk as ScenarioBatchEvent;
        options.onEvent?.(event);
        if (event.type === "progress") options.onProgress?.(event.completed, event.runs);
        if (event.type === "result") {
          const { type: _type, ...rest } = event;
          result = rest;
          options.onProgress?.(rest.completed, rest.runs);
        }
      }, options.signal);
      if (!result) throw new NcpError("unavailable", "The scenario batch ended without a result.");
      return result as ScenarioResult;
    },
    get: (id: string, options: { sample?: boolean } = {}) => bridge.call<ScenarioBatchDetail>("scenarios.get", { id, ...options }),
    list: async (options: { page?: string; space?: string; continuous?: boolean; limit?: number } = {}) =>
      (await bridge.call<{ batches: ScenarioBatchSummary[] }>("scenarios.list", options)).batches,
    /** Keeps one of your batches live: it regenerates on events with its tags or new input data, and flags breaches. */
    watch: (id: string, options: { continuous?: boolean; tags?: string[]; alertShare?: number; adjustments?: ScenarioBatchSpec["adjustments"] } = {}) =>
      bridge.call<{ id: string; continuous: boolean; triggerTags: string[]; alertShare: number }>("scenarios.watch", { id, ...options }),
  },
  /** Python (numpy, pandas, statsmodels, scikit-learn…) in a sandbox with no network: one of the page's compute/*.py scripts, or code. */
  compute: {
    run: <T = unknown>(target: { script: string } | { code: string }, options: { input?: unknown; files?: Record<string, string>; timeoutSeconds?: number; memoryMb?: number; entry?: string; language?: "python" | "excel" } = {}) =>
      bridge.call<ComputeOutcome<T>>("compute.run", { ...target, ...options }),
  },
  functions: {
    /** Runs one of the page's functions/*.ts server-side; T is what its default export returns. */
    run: <T = unknown>(name: string, input?: unknown) => bridge.call<{ output: T; logs: string[]; durationMs: number }>("functions.run", { name, input }),
  },
  events: {
    publish: (topic: string, payload?: Record<string, unknown>) => bridge.call<{ published: string }>("events.publish", { topic, payload }),
    /**
     * Platform events the host relays (page.<topic> from other pages and jobs, dataset changes): the type and identifiers only. A topic
     * ending in ".*" takes the family (stats.*, work.*), and "*" every event.
     */
    on: (topic: string, handler: (event: PlatformEvent) => void) => bridge.on("platform", (p) => {
      const e = p as PlatformEvent;
      if (typeof e?.type === "string" && topicMatches(topic, e.type)) handler(e);
    }),
  },
  /** Tools the page's agents (and the host copilot) can call to change what the user sees, declared in page.json clientTools. */
  tools: {
    register: (handlers: Record<string, ToolHandler>) => bridge.registerTools(handlers),
    invoke: (name: string, args?: unknown) => bridge.invokeTool(name, args),
    has: (name: string) => bridge.hasTool(name),
  },
  state: {
    set(patch: Record<string, unknown>) {
      pageState = { ...pageState, ...patch };
      pushState();
    },
    replace(value: Record<string, unknown>) {
      pageState = { ...value };
      pushState();
    },
    get: () => pageState,
  },
  ui: {
    toast: (message: string, options: { tone?: "info" | "success" | "warning" | "danger"; description?: string } = {}) => void bridge.host("toast", { message, ...options }).catch(() => undefined),
    confirm: (options: { title: string; body?: string; confirmLabel?: string; cancelLabel?: string; tone?: "default" | "danger" }) => bridge.host<boolean>("confirm", options),
    open: (target: OpenTarget) => bridge.host<void>("open", target),
    setTitle: (title: string) => void bridge.host("title", { title }).catch(() => undefined),
    download: async (name: string, data: Blob | ArrayBuffer | string, type = "application/octet-stream") =>
      bridge.host<void>("download", { name, type: data instanceof Blob && data.type ? data.type : type, data: await toArrayBuffer(data) }),
    print: () => window.print(),
    /** For inline slots (home widgets, matter tabs): the frame takes the page's height. */
    autoResize(): () => void {
      const root = document.documentElement;
      let last = 0;
      const measure = () => {
        // The document's own height, not scrollHeight: scrollHeight is never less than the frame, so the frame could not shrink.
        const h = Math.ceil(root.getBoundingClientRect().height);
        if (Math.abs(h - last) > 2) {
          last = h;
          void bridge.host("resize", { height: h }).catch(() => undefined);
        }
      };
      const observer = new ResizeObserver(measure);
      observer.observe(root);
      measure();
      const off = bridge.on("viewport", (v) => {
        const { top, height } = (v ?? {}) as { top?: number; height?: number };
        if (typeof top !== "number" || typeof height !== "number") return;
        root.style.setProperty("--ncp-vtop", `${top}px`);
        root.style.setProperty("--ncp-vh", `${height}px`);
      });
      return () => {
        observer.disconnect();
        off();
        root.style.removeProperty("--ncp-vtop");
        root.style.removeProperty("--ncp-vh");
      };
    },
  },
  nav: {
    route: (): PageRoute => currentRoute,
    /** Puts the page's own view in the host URL so links and the back button work. */
    set: (route: Partial<PageRoute> & { replace?: boolean }) => {
      currentRoute = { path: route.path ?? currentRoute.path, params: route.params ?? currentRoute.params };
      return bridge.host<void>("route", { ...currentRoute, replace: route.replace ?? false });
    },
    onChange: (handler: (route: PageRoute) => void) => bridge.on("route", (r) => {
      currentRoute = r as PageRoute;
      handler(currentRoute);
    }),
  },
  media: {
    /** Records audio through the host (the sandbox has no microphone). */
    record: async (options: { maxSeconds?: number; prompt?: string } = {}) => {
      const r = await bridge.host<{ data: ArrayBuffer; type: string; durationMs: number }>("media.record", options);
      return { blob: new Blob([r.data], { type: r.type }), durationMs: r.durationMs };
    },
  },
  files: {
    pick: async (options: { accept?: string; multiple?: boolean } = {}) => {
      const picked = await bridge.host<Array<{ name: string; type: string; data: ArrayBuffer }>>("files.pick", options);
      return picked.map((f) => new File([f.data], f.name, { type: f.type }));
    },
  },
  modes: {
    set: (mode: PageMode) => bridge.host<void>("mode", { mode }),
    onChange: (handler: (mode: PageMode) => void) => bridge.on("mode", (m) => handler(m as PageMode)),
  },
  /**
   * The host's display settings (contrast, motion, transparency, density, scheme, text size), or null from a host that sends none
   * (shell-ia §9.1). Page-kit's PageRoot applies them to the document; a page drawing its own canvas reads them here.
   */
  display: {
    get: (): HostDisplay | null => hostDisplay,
    onChange: (handler: (display: HostDisplay) => void) => bridge.on("display", (d) => handler(d as HostDisplay)),
  },
  /**
   * Raises the host's classification banner while the page shows material above its desk's floor (a Restricted reading), and drops the
   * raise with `null`. The host shows the highest marking on screen and never lowers its own floor (shell-ia §10.2).
   */
  classification: {
    raise: (level: Sensitivity | null, source?: string) => bridge.host<void>("classification", { level, source } satisfies HostClassificationArgs).catch(() => undefined),
    /** The desk's floor as the host sent it (its space manifest), or null. */
    floor: (): Sensitivity | null => bridge.current?.classification ?? null,
  },
  /** `"host"` when the host draws the page's header (the ECONIC desk header): the page then hides its own title block. */
  chromeHeader: (): "host" | null => (bridge.current?.chrome?.header === "host" ? "host" : null),
  on: bridge.on.bind(bridge),
  context: () => bridge.call<PlatformContext>("context.get", {}),
  usage: (kind: "view" | "action" | "tool" | "error" | "close", detail?: string, durationMs?: number) =>
    void bridge.call("pages.usage", { kind, detail, durationMs }).catch(() => undefined),
  /** The English or Arabic text for the session's locale. */
  t: (en: string, ar?: string | null) => (bridge.locale === "ar" && ar ? ar : en),
};

let currentRoute: PageRoute = { path: "", params: {} };
let hostDisplay: HostDisplay | null = null;
void bridge.session.then((s) => {
  currentRoute = s.route ?? currentRoute;
  hostDisplay = s.display ?? null;
}, () => undefined);
bridge.on("display", (d) => {
  if (d && typeof d === "object") hostDisplay = d as HostDisplay;
});

// The person working inside the frame counts for the host's idle clock (shell-ia §11.2, security §3.3): typing, pointing or scrolling here
// sends the host an `activity` heartbeat, at most one a minute. Only in a page's frame, and only once the host has answered.
const ACTIVITY_EVERY_MS = 60_000;
if (typeof document !== "undefined" && typeof window !== "undefined" && window.parent !== window) {
  let lastBeat = 0;
  const beat = () => {
    const now = Date.now();
    if (now - lastBeat < ACTIVITY_EVERY_MS || !bridge.current) return;
    lastBeat = now;
    void bridge.host("activity", {}).catch(() => undefined);
  };
  for (const name of ["keydown", "pointerdown", "wheel", "input", "touchstart"]) document.addEventListener(name, beat, { capture: true, passive: true });
}

// Links between a page's own HTML entries keep the session's language and theme (they travel in the frame URL's query). Only in a page's
// frame: the workspace imports the SDK too (embed mode) and routes its own links.
if (typeof document !== "undefined" && window.parent !== window) {
  document.addEventListener("click", (e) => {
    const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a || a.target || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const url = new URL(a.href, window.location.href);
    if (url.origin !== window.location.origin || !/\.html?$/i.test(url.pathname) || url.search || !window.location.search) return;
    e.preventDefault();
    window.location.href = `${url.pathname}${window.location.search}${url.hash}`;
  });
  // The frame may not open windows (its sandbox has no allow-popups), so a link out of the platform opens through the host.
  const openOut = (e: MouseEvent) => {
    const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a || e.defaultPrevented || e.button > 1) return;
    const url = new URL(a.href, window.location.href);
    if (url.origin === window.location.origin || !/^https?:$/.test(url.protocol)) return;
    e.preventDefault();
    void ncp.ui.open({ url: url.href }).catch(() => undefined);
  };
  document.addEventListener("click", openOut);
  document.addEventListener("auxclick", openOut);
}

export type Ncp = typeof ncp;
export default ncp;

function topicMatches(topic: string, type: string): boolean {
  if (topic === "*") return true;
  if (!topic.endsWith(".*")) return type === topic || type === `page.${topic}`;
  const family = topic.slice(0, -1);
  return type.startsWith(family) || type.startsWith(`page.${family}`);
}
